import { useBemCN } from "@gik/core/utils/bemBlock";
import { Separator } from "@gik/ui-legacy/Separator";
import React from "react";
import type { InjectedColorProps } from "react-color";
import { CustomPicker } from "react-color";
import { Hue, Saturation } from "react-color/lib/components/common";
import { PickerPointer } from "./PickerPointer";

export type BaseColorPickerProps = {
	// Optional chrome rendered above the shared saturation/hue section (e.g. the
	// solid preview + hex field). Receives the injected react-color props.
	renderHeader?: (props: InjectedColorProps) => React.ReactNode;
};

export type BaseColorPickerPropsInner = BaseColorPickerProps &
	InjectedColorProps;

export const BaseColorPicker = CustomPicker<BaseColorPickerProps>(
	({ renderHeader, ...pickerProps }: BaseColorPickerPropsInner) => {
		const bem = useBemCN("base-color-picker");

		const onSaturationChange = React.useCallback(
			(color) => {
				pickerProps?.onChange?.(color);
			},
			[pickerProps],
		);

		const onHueChange = React.useCallback(
			(color) => {
				pickerProps?.onChange?.({
					...pickerProps.hsl,
					h: color.h,
				});
			},
			[pickerProps],
		);

		return (
			<div {...bem()}>
				{renderHeader?.(pickerProps)}
				<Separator {...bem("separator")} />
				<div {...bem("label")}>Color Picker</div>
				<div {...bem("picker-block-wrapper", "saturation")}>
					<Saturation
						{...pickerProps}
						onChange={onSaturationChange}
						pointer={PickerPointer}
					/>
				</div>
				<div {...bem("picker-block-wrapper", "hue")}>
					<div
						{...bem("indicator")}
						style={{
							backgroundColor: pickerProps.hex,
						}}
					/>
					<div {...bem("hue")}>
						<Hue
							{...pickerProps}
							onChange={onHueChange}
							pointer={PickerPointer}
							direction="horizontal"
						/>
					</div>
				</div>
			</div>
		);
	},
);
